import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';

import GetManufacturerFromBrand from '../../used-vue/api/GetManufacturerFromBrand';
import Finance from './modules/finance';
import CustomerData from '../../used-vue/store/modules/customer-data';
import { getDefaultState } from './modules/defaultState';
import { doesAnythingNeedPosting } from './modules/doesAnythingNeedPosting';

Vue.use(Vuex);

const state = getDefaultState();

// Last posted state is basically a copy of the default state
state.lastPostedState = JSON.parse(JSON.stringify(state));

const store = new Vuex.Store({
    modules: {
        Finance,
        CustomerData
    },
    state,
    mutations: {
        setBrand: (state, brand) => {
            state.brand = brand;
        },
        setManufacturer: (state, brand) => {
            state.manufacturer = brand === 'ds' ? 'DS' : GetManufacturerFromBrand(brand);
        },
        setModel: (state, model) => {
            model.model_title = model.model_title.replace('Citroen', 'Citroën')
                .replace('fiat', 'Fiat')
                .replace('abarth', 'Abarth');
            state.model = model;
        },
        setAvailableGearboxes: (state, availableGearboxes) => {
            state.availableGearboxes = availableGearboxes;
        },
        setGearBoxChoice: (state, gearbox) => {
            state.gearboxChoice = gearbox;
        },
        setFuelTypeChoice: (state, fuelType) => {
            state.fuelTypeChoice = fuelType;
        },
        setTrimChoice: (state, trim) => {
            state.trimChoice = trim;
        },
        setTrimYear: (state, year) => {
            state.trimYear = year;
        },
        setEngineChoice: (state, engine) => {
            state.engineChoice = engine;
        },
        setVehicleId: (state, vehicleId) => {
            state.vehicleId = vehicleId;
        },
        setPaintChoice: (state, paintChoice) => {
            state.paintChoice = paintChoice;
        },
        setInteriorChoice: (state, interior) => {
            state.interiorChoice = interior;
        },
        setConfiguratorId: (state, id) => {
            state.configuratorId = id;
        },
        setFirstName: (state, firstName) => {
            state.firstName = firstName;
        },
        setLastName: (state, lastName) => {
            state.lastName = lastName;
        },
        setEmail: (state, email) => {
            state.email = email;
        },
        setCustomerDetailsSubmitted: (state, submitted) => {
            state.customerDetailsSubmitted = submitted;
        },
        setLastPostedData: (state) => {
            state.lastPostedState = JSON.parse(JSON.stringify(state));
            delete state.lastPostedState.lastPostedState;
        },
        setPaintOptions: (state, paintOptions) => {
            state.paintOptions = paintOptions;
        },
        setAvailableFuelTypes: (state, availableFuelTypes) => {
            state.availableFuelTypes = availableFuelTypes;
            state.availableFuelTypesAreFor = {
                model_id: state.model.id,
                gearboxChoice: state.gearboxChoice,
                fuelTypeChoice: state.fuelTypeChoice,
            };
        },
        setAvailableTrims: (state, availableTrims) => {
            state.availableTrims = availableTrims;
            state.availableTrimsAreFor = {
                model_id: state.model.id,
                gearboxChoice: state.gearboxChoice,
                fuelTypeChoice: state.fuelTypeChoice,
            };
        },
        setAvailableEngines: (state, availableEngines) => {
            state.availableEngines = availableEngines;
            state.availableEnginesAreFor = {
                model_id: state.model.id,
                gearboxChoice: state.gearboxChoice,
                fuelTypeChoice: state.fuelTypeChoice,
                trimChoice: state.trimChoice
            };
        },
        setAvailablePaints: (state, availablePaints) => {
            state.availablePaints = availablePaints;
            state.availablePaintsAreFor = state.vehicleId;
        },
        setAvailableInteriors: (state, availableInteriors) => {
            state.availableInteriors = availableInteriors;
            state.availableInteriorsAreFor = state.vehicleId;
        },
        setSelectedVehiclePrice: (state, selectedVehiclePrice) => {
            state.selectedVehiclePrice = selectedVehiclePrice;
        },
        setSelectedPaintPrice: (state, selectedPaintPrice) => {
            if (typeof selectedPaintPrice === 'string') {
                selectedPaintPrice = parseFloat(selectedPaintPrice);
            }
            if (isNaN(selectedPaintPrice) || typeof selectedPaintPrice !== 'number') {
                console.error('Invalid selectedPaintPrice:', selectedPaintPrice);
                return;
            }
            state.selectedPaintPrice = selectedPaintPrice;
        },
        setSelectedInteriorName: (state, selectedInteriorName) => {
            state.selectedInteriorName = selectedInteriorName.length > 25 ? selectedInteriorName.split("-")[0] : selectedInteriorName;
        },
        setSelectedInteriorPrice: (state, selectedInteriorPrice) => {
            state.selectedInteriorPrice = selectedInteriorPrice;
        },
        updateFinanceRequests: (state) => {
            if (state.selectedNewVehicle == null) {
                state.pcpVehicleRequest = null;
                state.hpVehicleRequest = null;
                return;
            }

            if (state.selectedNewVehicle.titre_code.length <= 0) {
                state.selectedNewVehicleIdentifierType = "CapShortCode";
                state.selectedNewVehicleIdentifier = state.selectedNewVehicle.cap_id;
            } else if (state.selectedNewVehicle.brand == "peugeot" || state.selectedNewVehicle.brand == "citroen" || state.selectedNewVehicle.brand == "ds") {
                state.selectedNewVehicleIdentifierType = "Titre";
                state.selectedNewVehicleIdentifier = state.selectedNewVehicle.titre_code;
            } else {
                state.selectedNewVehicleIdentifierType =  "CapShortCode";
                state.selectedNewVehicleIdentifier = state.selectedNewVehicle.cap_id;
            }

            let pcpPrice = parseFloat(state.selectedNewVehicle.price_PCP > 0 ? state.selectedNewVehicle.price_PCP : state.selectedNewVehicle.pricing_display_price);
            let pcpDiscount = parseFloat(state.selectedNewVehicle.pcp_discount_percent);
            let cleanSelectedPaintPrice = parseFloat((state.selectedPaintPrice == null) ? 0.0 : state.selectedPaintPrice);
            let cleanSelectedInteriorPrice = parseFloat((state.selectedInteriorPrice == null) ? 0.0 : state.selectedInteriorPrice);

            if (pcpDiscount > 1) {
                pcpDiscount = pcpDiscount / 100;
            }
            if (pcpDiscount > 0) {
                pcpDiscount = 1 - pcpDiscount;
                pcpPrice = pcpPrice + pcpDiscount * (cleanSelectedPaintPrice + cleanSelectedInteriorPrice);
            } else {
                pcpPrice = pcpPrice + cleanSelectedPaintPrice + cleanSelectedInteriorPrice;
            }

            state.pcpVehicleRequest = {
                Id: state.selectedNewVehicle.id + "-PCP",
                Vehicle: {
                    CashPrice: pcpPrice,
                    RegistrationDate: state.selectedNewVehicle.registrationDate,
                    IsNew: true,
                    Identifier: state.selectedNewVehicleIdentifier,
                    IdentifierType: state.selectedNewVehicleIdentifierType,
                    Type: state.selectedNewVehicle.carOrLCV,
                    // "ImageUrl": image,
                    DealerVehicleUrl: window.location.href,
                },
                Products: [{ Key: "PCP" }],
            };

            let hpPrice = parseFloat(state.selectedNewVehicle.price_HP > 0 ? state.selectedNewVehicle.price_HP : state.selectedNewVehicle.pricing_display_price);
            let hpDiscount = parseFloat(state.selectedNewVehicle.hp_discount_percent);
            if (hpDiscount > 1) {
                hpDiscount = hpDiscount / 100;
            }
            if (hpDiscount > 0) {
                hpDiscount = 1 - hpDiscount;
                hpPrice = hpPrice + hpDiscount * (cleanSelectedPaintPrice + cleanSelectedInteriorPrice);
            } else {
                hpPrice = hpPrice + cleanSelectedPaintPrice + cleanSelectedInteriorPrice;
            }

            state.hpVehicleRequest = {
                Id: state.selectedNewVehicle.id + "-HP",
                Vehicle: {
                    CashPrice: hpPrice,
                    RegistrationDate: state.selectedNewVehicle.registrationDate,
                    IsNew: true,
                    Identifier: state.selectedNewVehicleIdentifier,
                    IdentifierType: state.selectedNewVehicleIdentifierType,
                    Type: state.selectedNewVehicle.carOrLCV,
                    // "ImageUrl": image,
                    DealerVehicleUrl: window.location.href,
                },
                Products: [{ Key: "HP" }],
            };
        },
        setNewVehicle: (state, selectedNewVehicle) => {
            state.selectedNewVehicle = selectedNewVehicle;
        },
    },
    getters: {},
    actions: {
        getNewModel({ commit, state }, modelAndBrandObject) {
            return new Promise((resolve, reject) => {
                // If we already have the model data then we don't need to do anything
                if (state.model !== null && typeof state.model.url_model !== 'undefined' && state.model.url_model === modelAndBrandObject.urlModel) {
                    resolve(state.model);
                    return;
                }

                axios.get(`/api/configurator/gearboxes/${modelAndBrandObject.urlModel}`, {
                    params: {
                        brand: modelAndBrandObject.brand,
                    }
                }).then(response => {
                    commit('setBrand', modelAndBrandObject.brand);
                    commit('setManufacturer', modelAndBrandObject.brand);
                    commit('setModel', response.data.newModel);

                    if (typeof response.data.newModel.new_vehicles[0] !== 'undefined') {
                        commit('setPaintOptions', response.data.newModel.new_vehicles[0].new_vehicle_paint_options);
                        commit('setSelectedVehiclePrice', response.data.newModel.new_vehicles[0].pricing_display_price);
                    }

                    commit('setAvailableGearboxes', response.data.gearboxes);
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            });
        },

        getFuelTypes({ commit, state }) {
            return new Promise((resolve, reject) => {
                // If we've already collected the fuel types data for this model, then we don't need to collect it again
                if (state.availableFuelTypesAreFor &&
                    state.availableFuelTypesAreFor.model_id === state.model.id &&
                    state.availableFuelTypesAreFor.gearboxChoice === state.gearboxChoice
                ) {
                    resolve(state.availableFuelTypes);
                    return;
                }

                axios.get(`/api/configurator/fuel/${state.model.id}`, {
                    params: {
                        transmission: state.gearboxChoice,
                    }
                }).then(response => {
                    commit('setAvailableFuelTypes', response.data.fuelTypes);
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            });
        },

        getTrims({ commit, state }) {
            return new Promise((resolve, reject) => {
                // If we've already collected the trims' data for this model, then we don't need to collect it again
                if (state.availableTrimsAreFor &&
                    state.availableTrimsAreFor.model_id === state.model.id &&
                    state.availableTrimsAreFor.gearboxChoice === state.gearboxChoice &&
                    state.availableTrimsAreFor.fuelTypeChoice === state.fuelTypeChoice
                ) {
                    resolve(state.availableTrims);
                    return;
                }

                axios.get(`/api/configurator/trims/${state.model.id}`, {
                    params: {
                        transmission: state.gearboxChoice,
                        fuel: state.fuelTypeChoice
                    }
                }).then(response => {
                    commit('setAvailableTrims', response.data);
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            });
        },

        getEngines({ commit, state }) {
            return new Promise((resolve, reject) => {
                // If we've already collected the engines' data for this model, then we don't need to collect it again
                if (state.availableEnginesAreFor &&
                    state.availableEnginesAreFor.model_id === state.model.id &&
                    state.availableEnginesAreFor.gearboxChoice === state.gearboxChoice &&
                    state.availableEnginesAreFor.fuelTypeChoice === state.fuelTypeChoice &&
                    state.availableEnginesAreFor.trimChoice === state.trimChoice
                ) {
                    resolve(state.availableEngines);
                    return;
                }

                axios.get(`/api/configurator/engines/${state.model.id}`, {
                    params: {
                        transmission: state.gearboxChoice,
                        fuel: state.fuelTypeChoice,
                        trim: state.trimChoice
                    }
                }).then(response => {
                    commit('setAvailableEngines', response.data);
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            });
        },

        getPaints({ commit, state }) {
            return new Promise((resolve, reject) => {
                // If we've already collected the paints for this model, then we don't need to collect it again
                if (state.vehicleId !== null && state.setAvailablePaintsAreFor === state.vehicleId) {
                    resolve(state.availablePaints);
                    return;
                }

                axios.get(`/api/configurator/paints/${state.vehicleId}`).then(response => {
                    state.availableEngines.forEach(element => {
                        if (element.id !== state.vehicleId) {
                            return;
                        }
                    });
                    commit('setAvailablePaints', response.data.new_vehicle_paint_options);
                    commit('setPaintOptions', response.data.new_vehicle_paint_options);
                    commit('setSelectedVehiclePrice', response.data.pricing_display_price);
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            });
        },

        getInteriors({ commit, state }) {
            return new Promise((resolve, reject) => {
                // If we've already collected the interiors for this model, then we don't need to collect it again
                if (state.vehicleId !== null && state.availableInteriorsAreFor === state.vehicleId) {
                    resolve(state.availableInteriors);
                    return;
                }

                axios.get(`/api/configurator/interiors/${state.vehicleId}`).then(response => {
                    commit('setAvailableInteriors', response.data);
                    resolve(response.data);
                }).catch(error => {
                    reject(error);
                });
            });
        },

        syncDatabase({ commit, dispatch, state, rootState }, query) {
            return new Promise((resolve, reject) => {
                if (typeof query.id !== 'undefined' && query.id !== state.configuratorId) {
                    dispatch('getConfigById', query.id)
                        .then(response => {
                            resolve({
                                id: response.id,
                                redirect: true
                            });
                        }).catch(error => {
                            reject(error);
                        });

                    return;
                }

                // To prevent POSTing to store twice, check that something has changed before POSTing
                if (!doesAnythingNeedPosting(state, rootState)) {
                    resolve({
                        id: state.configuratorId,
                        redirect: false,
                    });
                    return;
                }

                axios.post('/api/configurator/store', state).then(response => {
                    if (state.configuratorId === null) {
                        commit('setConfiguratorId', response.data.data.id);
                    }
                    commit('setLastPostedData');

                    resolve({
                        id: response.data.data.id,
                        redirect: false,
                    });
                }).catch(error => {
                    reject(error);
                });
            });
        },

        getNewVehicleById({commit, state}, id) {
            return new Promise((resolve, reject) => {
                if (state.vehicleId == null) {
                    console.error("Cannot load new vehicle, no ID is set");
                    reject();
                    return;
                }
                // No need to contact the API if we already have the data
                if (state.selectedNewVehicle != null && state.selectedNewVehicle.id == state.vehicleId) {
                    resolve(state.selectedNewVehicle);
                    return;
                }

                axios.get('/api/configurator/new-vehicle/' + state.vehicleId).then(response => {
                    const data = response.data.data;
                    if (typeof data != 'undefined') {
                        commit('setNewVehicle', data);
                        commit('updateFinanceRequests');
                    } else {
                        commit('setNewVehicle', null);
                        commit('updateFinanceRequests');
                    }

                    resolve(data);
                }).catch(error => {
                    reject(error);
                });
            });
        },

        getConfigById({ commit, dispatch }, id) {
            return new Promise((resolve, reject) => {
                axios.get('/api/configurator', {
                    params: {
                        configuratorId: id
                    }
                }).then(response => {
                    const data = response.data.data;

                    dispatch('getNewModel', {
                        brand: data.brand,
                        urlModel: data.new_model.url_model
                    }).then(() => {
                        commit('setConfiguratorId', data.id);
                        commit('setBrand', data.brand);
                        commit('setGearBoxChoice', data.gearbox);
                        commit('setFuelTypeChoice', data.fuel_type);
                        commit('setTrimChoice', data.trim);
                        commit('setEngineChoice', data.engine);
                        commit('setVehicleId', data.new_vehicle_id);
                        commit('setPaintChoice', data.new_vehicle_paint_id);
                        commit('setInteriorChoice', data.new_vehicle_interior_id);
                        commit('setCustomerDetailsSubmitted', data.customer_details_submitted);
                        commit('setLastPostedData');

                        if (typeof data.newVehicle != 'undefined') {
                            commit('setNewVehicle', data.newVehicle);
                            commit('setSelectedVehiclePrice', data.newVehicle.pricing_display_price);
                            if (typeof data.newVehicle.new_vehicle_specs != 'undefined' && typeof data.newVehicle.new_vehicle_specs[0] != 'undefined') {
                                commit("setSelectedInteriorName", data.newVehicle.new_vehicle_specs[0].name);
                                commit("setSelectedInteriorPrice", data.newVehicle.new_vehicle_specs[0].price);
                            }
                            if (typeof data.newVehicle.new_vehicle_paint_options != 'undefined' && typeof data.newVehicle.new_vehicle_paint_options[0] != 'undefined') {
                                commit('setAvailablePaints', data.newVehicle.new_vehicle_paint_options);
                                commit('setPaintOptions', data.newVehicle.new_vehicle_paint_options);
                                commit("setSelectedPaintPrice", data.newVehicle.new_vehicle_paint_options[0].price);
                            }
                        } else {
                            commit('setNewVehicle', null);
                        }

                        commit('updateFinanceRequests');

                        // if (data.new_vehicle_id && data.new_vehicle_paint_id) {
                        //     dispatch('getPaints').then((response) => {
                        //         response.new_vehicle_paint_options.map(paint => {
                        //             if (paint.id === data.new_vehicle_paint_id) {
                        //                 commit('setSelectedPaintPrice', paint.price);
                        //             }
                        //         });
                        //         resolve(data);
                        //     }).catch(e => reject(e));
                        //     return;
                        // }

                        resolve(data);
                    });
                }).catch(error => {
                    reject(error);
                });
            });
        }
    },
});

export default store;